import React, { useState, useEffect, useContext } from "react";
import {
  NavLink,
  Routes,
  Route,
  useLocation,
  useNavigate
} from "react-router-dom";

import { logout } from "../../utils/auth";

import {
  LayoutDashboard,
  Bell,
  FileBarChart2,
  Camera,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  Crown,
} from "lucide-react";

import Dashboard from "./presDashboard";
import CommandDashboard from "./commandDashboard";
import CrowdAnalytics from "./crowdanalytics";
import Alerts from "./presAlert";
import Report from "./preReport";
import Livecamera from "./livecamera";
import SampleCamera from "./samplecamera";
import RulesZone from "./presRuleszone";
import NVR from "./nvr";

import AlertNotification from "../../components/AlertNotification";
import alertWebSocketService from "../../services/alertWebSocket";
import { securePath, decodeSecurePath, useSecureNavigate } from "../../utils/secureRoutes";
import { RootContext } from "../../context/RootContext";

const BASE_PATH = "/prestigehome";

/*
|--------------------------------------------------------------------------
| Route keys
|--------------------------------------------------------------------------
|
| Defined locally rather than imported from a shared routes constant.
|--------------------------------------------------------------------------
*/

const ROUTE_KEYS = {
  dashboard: "dashboard",
  commandDashboard: "command-dashboard",
  crowdAnalytics: "crowd-analytics",
  alerts: "alerts",
  reports: "reports",
  liveCamera: "live-camera",
  sampleCamera: "sample-camera",
  rulesZone: "rules-zone",
  nvr: "nvr",
};

/*
|--------------------------------------------------------------------------
| Route -> Component map
|--------------------------------------------------------------------------
|
| Keyed by the raw route key. Every page now shares the same pathname
| (BASE_PATH) — the actual route is decoded from the query string — so
| this map is what SecureRouteResolver uses to pick a component once it
| has decoded the current URL.
|
|--------------------------------------------------------------------------
*/

const routeComponentMap = {
  [ROUTE_KEYS.dashboard]: Dashboard,
  [ROUTE_KEYS.commandDashboard]: CommandDashboard,
  [ROUTE_KEYS.crowdAnalytics]: CrowdAnalytics,
  [ROUTE_KEYS.alerts]: Alerts,
  [ROUTE_KEYS.reports]: Report,
  [ROUTE_KEYS.liveCamera]: Livecamera,
  [ROUTE_KEYS.sampleCamera]: SampleCamera,
  [ROUTE_KEYS.rulesZone]: RulesZone,
  [ROUTE_KEYS.nvr]: NVR,
};

/*
|--------------------------------------------------------------------------
| Nav items
|--------------------------------------------------------------------------
|
| Each item keeps its plain routeKey alongside the secure `path`, because
| with every page sharing the same pathname now, NavLink's built-in
| pathname-only "active" matching can't tell them apart — every link would
| render as active. Active state is computed manually below instead, by
| decoding the current URL and comparing routeKey.
|--------------------------------------------------------------------------
*/

const navItems = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    routeKey: ROUTE_KEYS.dashboard,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.dashboard}`),
  },

  {
    label: "Command Dashboard",
    icon: LayoutDashboard,
    routeKey: ROUTE_KEYS.commandDashboard,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.commandDashboard}`),
  },

  {
    label: "Crowd Analytics",
    icon: FileBarChart2,
    routeKey: ROUTE_KEYS.crowdAnalytics,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.crowdAnalytics}`),
  },

  {
    label: "Alerts",
    icon: Bell,
    routeKey: ROUTE_KEYS.alerts,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.alerts}`),
  },

  {
    label: "Reports",
    icon: FileBarChart2,
    routeKey: ROUTE_KEYS.reports,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.reports}`),
  },

  {
    label: "Live Cameras",
    icon: Camera,
    routeKey: ROUTE_KEYS.liveCamera,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.liveCamera}`),
  },

  {
    label: "Live Monitoring",
    icon: Camera,
    routeKey: ROUTE_KEYS.sampleCamera,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.sampleCamera}`),
  },

  {
    label: "Rules & Zones",
    icon: Shield,
    routeKey: ROUTE_KEYS.rulesZone,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.rulesZone}`),
  },

  {
    label: "NVR",
    icon: Camera,
    routeKey: ROUTE_KEYS.nvr,
    path: securePath(`${BASE_PATH}/${ROUTE_KEYS.nvr}`),
    premium: true,
    disabled: true,
  },
];

const DEFAULT_SECURE_PATH = navItems[0].path;

/*
|--------------------------------------------------------------------------
| Decode the current URL's route key
|--------------------------------------------------------------------------
*/

const useActiveRouteKey = () => {
  const location = useLocation();

  const decoded = decodeSecurePath(location.pathname, location.search);

  return decoded
    ? decoded.replace(`${BASE_PATH}/`, "").replace(/^\//, "")
    : "";
};

/*
|--------------------------------------------------------------------------
| SECURE ROUTE RESOLVER
|--------------------------------------------------------------------------
|
| Every page shares the pathname BASE_PATH; the real route is decoded from
| the ?rh=... query param. Anything that fails to decode, or doesn't match
| a known route, falls back to the dashboard.
|--------------------------------------------------------------------------
*/

const SecureRouteResolver = () => {
  const routeKey = useActiveRouteKey();

  const Component = routeComponentMap[routeKey] ?? Dashboard;

  return <Component />;
};

/*
|--------------------------------------------------------------------------
| PRESTIGE LAYOUT
|--------------------------------------------------------------------------
*/

const Layout = () => {
  const navigate = useNavigate();
  const secureNavigate = useSecureNavigate();
  const activeRouteKey = useActiveRouteKey();

  const [collapsed, setCollapsed] = useState(false);
  const { dispatch } = useContext(RootContext);

  /*
  |--------------------------------------------------------------------------
  | WebSocket
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const citriot = JSON.parse(
      localStorage.getItem("citriot")
    );

    const organizationId =
      localStorage.getItem("organizationId");

    if (citriot?.auth?.user && organizationId) {
      alertWebSocketService.connect(
        organizationId,
        citriot.auth.user
      );

      const timeout = setTimeout(() => {
        alertWebSocketService.getStatus();
      }, 2000);

      return () => {
        clearTimeout(timeout);
        alertWebSocketService.disconnect();
      };
    }

    return () => {
      alertWebSocketService.disconnect();
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  |
  | /login sits outside the secure-route scheme entirely, so this is a
  | plain redirect rather than going through useSecureNavigate.
  |--------------------------------------------------------------------------
  */

  const handleLogout = async () => {
      await logout();
      dispatch({ type: "LOGOUT" });
      navigate("/login", { replace: true });
    };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans">

      {/* ============================================================
          TOP BAR
      ============================================================ */}

      <header className="sticky top-0 z-50 h-[60px] flex items-center px-6 backdrop-blur-xl bg-white/95 border-b border-slate-200">

        {/* Logo */}

        <div className="flex items-center shrink-0">

          <div className="flex items-center justify-center">
            <img
              src="/logo.png"
              alt="Logo"
              className="h-[42px] w-auto object-contain"
            />
          </div>

          <div className="mx-5 h-8 w-px bg-slate-200" />

          <div className="flex flex-col">

            <span className="text-[15px] font-bold tracking-[-0.3px] text-slate-900">
              CogniVision
            </span>

            <span className="text-[10px] font-semibold tracking-[0.18em] text-slate-500 uppercase">
              Next Gen Video Analytics Platform
            </span>

          </div>

        </div>

        {/* Right Side */}

        <div className="ml-auto flex items-center gap-4">

          <div className="flex items-center justify-center">

            <img
              src="/prestige.png"
              alt="Prestige"
              className="h-[42px] w-auto object-contain px-1"
            />

          </div>

          <div className="h-8 w-px bg-slate-200" />

          <button
            onClick={handleLogout}
            className="
              flex items-center justify-center
              w-9 h-9
              rounded-lg
              border border-red-200
              bg-red-50
              text-red-600
              hover:bg-red-100
              transition-all
              hover:scale-105
            "
            title="Logout"
          >
            <LogOut size={18} />
          </button>

        </div>

      </header>

      {/* ============================================================
          BODY
      ============================================================ */}

      <div className="flex flex-1 overflow-hidden">

        {/* ============================================================
            SIDEBAR
        ============================================================ */}

        <aside
          className={`
            fixed
            top-[60px]
            left-0
            z-40
            h-[calc(100vh-60px)]
            bg-white/95
            border-r
            border-slate-200
            flex
            flex-col
            transition-all
            duration-300

            ${
              collapsed
                ? "w-16"
                : "w-[220px]"
            }
          `}
        >

          {/* Collapse Button */}

          <div
            className={`
              flex
              ${
                collapsed
                  ? "justify-center"
                  : "justify-end"
              }
              px-3
              pt-3
              pb-1
            `}
          >

            <button
              onClick={() =>
                setCollapsed(!collapsed)
              }
              className="
                w-7
                h-7
                rounded-md
                border
                border-slate-200
                bg-slate-100
                flex
                items-center
                justify-center
                text-slate-600
                hover:bg-slate-200
                transition-all
              "
              title={
                collapsed
                  ? "Expand sidebar"
                  : "Collapse sidebar"
              }
            >

              {collapsed ? (
                <ChevronRight size={14} />
              ) : (
                <ChevronLeft size={14} />
              )}

            </button>

          </div>

          {/* Navigation */}

          <nav className="flex-1 px-2 py-2 flex flex-col gap-1">

            {navItems.map((item) => {

              const isActive = item.routeKey === activeRouteKey;

              /*
              |--------------------------------------------------------------------------
              | DISABLED NAVIGATION
              |--------------------------------------------------------------------------
              */

              if (item.disabled) {

                return (
                  <div
                    key={item.routeKey}
                    className={`
                      flex
                      items-center

                      ${
                        collapsed
                          ? "justify-center"
                          : "justify-start"
                      }

                      ${
                        collapsed
                          ? "gap-0"
                          : "gap-2.5"
                      }

                      px-3
                      py-2.5
                      rounded-[10px]

                      text-[13px]
                      font-semibold

                      text-slate-400
                      bg-slate-50

                      cursor-not-allowed

                      border
                      border-transparent

                      opacity-70
                    `}
                  >

                    <item.icon
                      size={17}
                      color="#94a3b8"
                    />

                    {!collapsed && (
                      <>
                        <span className="flex-1">
                          {item.label}
                        </span>

                        {item.premium && (
                          <Crown
                            size={14}
                            className="text-amber-500"
                            title="Premium"
                          />
                        )}
                      </>
                    )}

                  </div>
                );
              }

              /*
              |--------------------------------------------------------------------------
              | NORMAL NAVIGATION
              |--------------------------------------------------------------------------
              |
              | NavLink is still used for its normal <a>-tag navigation
              | behavior, but its own isActive detection is ignored (it
              | only compares pathname, and every item shares BASE_PATH
              | now) — `isActive` above, derived from the decoded query
              | param, drives the styling instead.
              |--------------------------------------------------------------------------
              */

              return (
                <NavLink
                  key={item.routeKey}
                  to={item.path}
                  className={`
                    flex
                    items-center

                    ${
                      collapsed
                        ? "justify-center"
                        : "justify-start"
                    }

                    ${
                      collapsed
                        ? "gap-0"
                        : "gap-2.5"
                    }

                    px-3
                    py-2.5
                    rounded-[10px]

                    text-[13px]
                    font-semibold

                    transition-all
                    duration-150

                    border

                    ${
                      isActive
                        ? "bg-blue-50 text-blue-600 border-blue-100"
                        : "text-slate-500 border-transparent hover:bg-slate-100"
                    }
                  `}
                >

                  <item.icon
                    size={17}
                    color={
                      isActive
                        ? "#2563eb"
                        : "#64748b"
                    }
                  />

                  {!collapsed && (
                    <>
                      <span
                        className="flex-1"
                        style={{
                          color: isActive ? "#2563eb" : "#64748b",
                        }}
                      >
                        {item.label}
                      </span>

                      {item.premium && (
                        <Crown
                          size={14}
                          className="text-amber-500"
                          title="Premium"
                        />
                      )}
                    </>
                  )}

                </NavLink>
              );
            })}

          </nav>

        </aside>

        {/* ============================================================
            MAIN CONTENT
        ============================================================ */}

        <main
          className={`
            flex-1
            overflow-y-auto
            p-7
            min-h-[calc(100vh-60px)]
            bg-slate-50
            transition-all
            duration-300

            ${
              collapsed
                ? "ml-16"
                : "ml-[220px]"
            }
          `}
        >

        <Routes>
          {/*
            Every page lives at the same pathname now (BASE_PATH); the
            actual page is decided by decoding the ?rh=... query param,
            so a single catch-all route covers everything.
          */}
          <Route
            path="*"
            element={<SecureRouteResolver />}
          />
        </Routes>

        </main>

      </div>

      {/* ============================================================
          GLOBAL ALERT NOTIFICATION
      ============================================================ */}

      <AlertNotification />

    </div>
  );
};

export default Layout;